import { EMPTY_STR } from "@/constants/string";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";

/**
 * Fetch a docx template from a public URL, run docxtemplater with the given values, and return the rendered blob.
 *
 * `templateValues` is fed verbatim to `doc.render(...)` — keys must match the `{placeholder}` tags inside the docx
 * (table-row loops use `{#list}...{/list}` and expect an array on `list`).
 *
 * Null/undefined placeholder values render as `"-"` via the configured `nullGetter`. Conditional sections
 * (`{#x}...{/x}`) follow docxtemplater's default truthy check, so a `null`/empty value collapses the whole block
 * (used to suppress nickname brackets when the nickname is empty).
 *
 * Returns `null` when the template URL cannot be fetched (404 / non-docx response). All other failures
 * (XML parse errors, missing placeholders, etc.) throw so the caller can show an error.
 */
export const generateDocxBlobFromTemplate = async (templatePath: string, templateValues: unknown): Promise<Blob | null> => {
  const response = await fetch(templatePath);
  if (!response.ok) return null;

  const arrayBuffer = await response.arrayBuffer();
  const zip = new PizZip(arrayBuffer);
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    // tag (placeholder) は "-" にフォールバック、section (`{#x}...{/x}`) は null を返して section ごと崩す。
    // section に "-" を返すと truthy 扱いで section が展開され、空欄を消したい conditional が機能しない。
    nullGetter: (part: { module?: string }) => (part.module === "loop" ? null : EMPTY_STR.TEXT),
  });

  doc.render(templateValues);
  return doc.getZip().generate({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
};

export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
